/**
 * Pure, React-free formatting for the review queue and reviewer. Kept isolated so
 * the age/label/derivation logic is unit-testable away from the DOM.
 */
import type { DocumentView, ReviewQueueItem, ReviewReason } from '../../api/schema';
import { OTHER_CATEGORY_NAME } from '../../lib/category';

/** A trimmed, non-empty, non-sentinel class name — else `null`. */
function realClassName(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === OTHER_CATEGORY_NAME) return null;
  return trimmed;
}

/**
 * Compact age of a queued item, from its `created_at` to `now`:
 * `3d` · `22h` · `9h` · `4m` · `now`. Matches the design-reference §4.5 column.
 */
export function ageLabel(createdAt: string, now: number = Date.now()): string {
  const then = new Date(createdAt).getTime();
  if (Number.isNaN(then)) return '';
  const ms = Math.max(0, now - then);
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'now';
  const hours = Math.floor(minutes / 60);
  if (hours < 1) return `${String(minutes)}m`;
  const days = Math.floor(hours / 24);
  if (days < 1) return `${String(hours)}h`;
  return `${String(days)}d`;
}

/**
 * A longer, spoken age used in the reviewer header and screen-reader labels:
 * `3 days` · `22 hours` · `9 hours` · `4 minutes` · `under a minute`.
 */
export function ageSpoken(createdAt: string, now: number = Date.now()): string {
  const then = new Date(createdAt).getTime();
  if (Number.isNaN(then)) return '';
  const ms = Math.max(0, now - then);
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'under a minute';
  const hours = Math.floor(minutes / 60);
  if (hours < 1) return `${String(minutes)} minute${minutes === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  if (days < 1) return `${String(hours)} hour${hours === 1 ? '' : 's'}`;
  return `${String(days)} day${days === 1 ? '' : 's'}`;
}

/** Human label for a review reason (design-reference §4.5 reason badge). */
export function reasonLabel(reason: ReviewReason): string {
  switch (reason) {
    case 'low_confidence':
      return 'Low confidence';
    case 'content_filter':
      return 'Content filter';
    case 'manual':
      return 'Manual';
    case 'other':
      return 'Other';
  }
}

export type ReasonTone = 'warn' | 'bad' | 'muted';

/** Colour tone for a reason badge — never colour alone, always paired with its text. */
export function reasonTone(reason: ReviewReason): ReasonTone {
  switch (reason) {
    case 'low_confidence':
      return 'warn';
    case 'content_filter':
      return 'bad';
    case 'manual':
      return 'muted';
    case 'other':
      return 'muted';
  }
}

/**
 * Whether a suggested class is a proposed *new* category — one the classifier put
 * forward that is not yet in the effective taxonomy. Drives the `NEW` chip and the
 * "Create" (vs "Assign") accept action.
 */
export function isProposedNew(
  suggestedClass: string | null | undefined,
  categoryNames: Iterable<string>,
): boolean {
  if (suggestedClass == null || suggestedClass.trim() === '') return false;
  const known = new Set<string>();
  for (const name of categoryNames) known.add(name.trim().toLowerCase());
  return !known.has(suggestedClass.trim().toLowerCase());
}

/**
 * The name shown/acted on as "the model's suggestion" for one review item: the
 * model's own proposed-new-category name when it has one, else the document's
 * stored classification (falling back to the queue row's own `best_guess`) —
 * populated for a low-confidence item, which now stores the real category the
 * model named rather than the catch-all (see `app.workers.embed`'s
 * `_file_needs_review`). `null` when there is truly nothing to suggest.
 *
 * The `Other / Unclassified` sentinel is never returned: it is not a real
 * category, so offering it would let `isProposedNew` flag it as a brand-new
 * type and invite a reviewer to *create* it.
 */
export function resolveSuggestionName(
  item: ReviewQueueItem,
  document: DocumentView | null,
): string | null {
  return (
    realClassName(item.suggested_class) ??
    realClassName(document?.classification?.category_name) ??
    realClassName(item.document?.best_guess?.category_name)
  );
}

/**
 * The class a queued document already carries, for the queue's own column —
 * `null` when it has none or only the catch-all sentinel.
 *
 * Distinct from `resolveSuggestionName`: this is the document's *current*
 * class, not a proposal, so the queue renders it muted and without the `NEW`
 * chip. A low-confidence document is the case that has one (the model named a
 * real category; only the embedding-margin discount pulled it under
 * threshold), and it is deliberately not offered as a suggestion to accept.
 */
export function currentClassName(item: ReviewQueueItem): string | null {
  return realClassName(item.document?.best_guess?.category_name);
}

/** Two-decimal confidence for the mono read-out (`0.86`). */
export function fmtConfidence(value: number): string {
  return value.toFixed(2);
}

export interface SortOption {
  value: string;
  label: string;
}

/** The four server-backed sort modes of `GET /v1/review?sort=`. */
/**
 * The queue's default order: newest first.
 *
 * A backlog is read from the top, and the item that just arrived is the one a
 * reviewer is most likely to be looking for — the oldest-first default meant
 * opening the queue to the items that had already been passed over the longest.
 */
export const DEFAULT_SORT = 'age_desc';

export const SORT_OPTIONS: readonly SortOption[] = [
  { value: 'age_desc', label: 'Newest first' },
  { value: 'age', label: 'Oldest first' },
  { value: 'confidence', label: 'Lowest confidence' },
  { value: 'confidence_desc', label: 'Highest confidence' },
];

/** The reason filter values the server accepts, plus the "all" sentinel. */
export const REASON_FILTERS: readonly SortOption[] = [
  { value: 'all', label: 'All reasons' },
  { value: 'low_confidence', label: 'Low confidence' },
  { value: 'other', label: 'Other' },
  { value: 'content_filter', label: 'Content filter' },
  { value: 'manual', label: 'Manual' },
];
